/**
 * GP Free Shipping Bar — cart-reactive progress UI.
 *
 * Renders any number of .gp-fsb elements on the page (header bar, cart drawer
 * mini-bar, product page nudge). Each instance needs:
 *   - element with class="gp-fsb" and data-threshold
 *   - child .gp-fsb__message
 *   - child .gp-fsb__progress (for progress fill)
 *
 * IP-based suppression hits /api/geo on weship-shipping.vercel.app to
 * read Vercel's edge geolocation header. AK/HI visitors → all bars hidden.
 */
(function () {
  'use strict';

  const SUPPRESS_KEY = 'gp_fsb_suppressed';
  const REGION_KEY = 'gp_fsb_region';
  const REGION_TTL_MS = 24 * 60 * 60 * 1000;

  function fmt(n) {
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  function getBars() {
    return document.querySelectorAll('.gp-fsb');
  }

  function renderBar(bar, subtotalUsd, cartWeightLb) {
    const tGround   = parseFloat(bar.dataset.threshold)         || 300;
    const messageEl = bar.querySelector('.gp-fsb__message');
    const progressEl = bar.querySelector('.gp-fsb__progress');
    if (!messageEl) return;

    // Single advertised tier: Free Ground at the threshold. Air (2-Day / Next-Day) is now a
    // paid upgrade priced at the delta over ground (see api/shopify-rates) — never free — so
    // we no longer advertise free expedited tiers. cartWeightLb is unused now that no tiers
    // are weight-gated, but kept in the signature for callers.
    void cartWeightLb;
    const tiers = [{ name: 'Ground', threshold: tGround }];
    const nextTier = tiers.find(t => subtotalUsd < t.threshold);
    const highestUnlocked = [...tiers].reverse().find(t => subtotalUsd >= t.threshold);

    if (subtotalUsd <= 0) {
      // Idle state — advertise the entry tier
      messageEl.textContent = 'Free Ground shipping on orders ' + fmt(tGround) + '+';
      bar.classList.remove('gp-fsb--unlocked');
      if (progressEl) progressEl.style.width = '0%';
      return;
    }

    if (nextTier) {
      // Cart in progress — show distance to next tier
      const remaining = nextTier.threshold - subtotalUsd;
      const prevThreshold = highestUnlocked ? highestUnlocked.threshold : 0;
      const span = nextTier.threshold - prevThreshold;
      const pct = Math.min(100, ((subtotalUsd - prevThreshold) / span) * 100);
      if (highestUnlocked) {
        // Already unlocked at least one tier — celebrate + nudge to next
        messageEl.innerHTML =
          '<span class="gp-fsb__sparkle">✨</span>Free ' + highestUnlocked.name + ' unlocked — add ' +
          '<span class="gp-fsb__amount">' + fmt(remaining) + '</span> more for free ' + nextTier.name;
        bar.classList.add('gp-fsb--unlocked');
      } else {
        messageEl.innerHTML =
          'Add <span class="gp-fsb__amount">' + fmt(remaining) +
          '</span> more for free ' + nextTier.name + ' shipping';
        bar.classList.remove('gp-fsb--unlocked');
      }
      if (progressEl) progressEl.style.width = pct + '%';
    } else {
      // All visible tiers unlocked — celebrate the highest one shown.
      const top = tiers[tiers.length - 1];
      messageEl.innerHTML = '<span class="gp-fsb__sparkle">✨</span>Free ' + top.name + ' shipping unlocked';
      bar.classList.add('gp-fsb--unlocked');
      if (progressEl) progressEl.style.width = '100%';
    }
  }

  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return { subtotal: 0, weightLb: 0 };
      const cart = await r.json();
      return {
        subtotal: (cart.total_price || 0) / 100,
        weightLb: (cart.total_weight || 0) / 453.592,
      };
    } catch {
      return { subtotal: 0, weightLb: 0 };
    }
  }

  async function update() {
    const { subtotal, weightLb } = await fetchCart();
    getBars().forEach(bar => renderBar(bar, subtotal, weightLb));
  }

  async function shouldSuppress(geoEndpoint, suppressedStates) {
    // The permanent suppress flag outlived wrong answers from the edge-cached geo
    // lookup (fixed 2026-10-10); the 24h region cache below is the only memory now.
    try { localStorage.removeItem(SUPPRESS_KEY); } catch {}
    try {
      const cached = JSON.parse(localStorage.getItem(REGION_KEY) || 'null');
      if (cached && Date.now() - cached.t < REGION_TTL_MS) {
        return suppressedStates.includes((cached.region || '').toUpperCase());
      }
    } catch {}
    try {
      const r = await fetch(geoEndpoint);
      if (!r.ok) return false;
      const data = await r.json();
      const region = (data.region || '').toUpperCase();
      localStorage.setItem(REGION_KEY, JSON.stringify({ region, t: Date.now() }));
      return suppressedStates.includes(region);
    } catch {
      return false;
    }
  }

  async function init() {
    const bars = getBars();
    if (!bars.length) return;
    // Read suppression config from first bar (assume all share same settings)
    const first = bars[0];
    const geoEndpoint = first.dataset.geoEndpoint || 'https://weship-shipping.vercel.app/api/geo';
    const suppressedStates = (first.dataset.suppressedStates || 'AK,HI')
      .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    if (await shouldSuppress(geoEndpoint, suppressedStates)) {
      bars.forEach(b => b.remove());
      return;
    }
    bars.forEach(b => { b.hidden = false; });
    update();
  }

  // Cart change listeners (multiple event names cover most themes including Horizon)
  ['cart:updated', 'cart:refresh', 'theme:cart:change', 'theme:cart:reload'].forEach(evt => {
    document.addEventListener(evt, update);
  });
  window.addEventListener('focus', update);

  // Fetch interceptor — catches AJAX cart writes (add/change/update/clear) regardless of theme.
  // Also re-runs init for any bars that get added dynamically (e.g., when cart drawer opens).
  if (window.fetch) {
    const origFetch = window.fetch.bind(window);
    // Second param must not be named `init`: it shadowed the bar's init(), so
    // every cart write threw "init is not a function" and never re-ran it.
    window.fetch = function (input, opts) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isCartWrite = /\/cart\/(add|change|update|clear)/.test(url);
      const promise = origFetch(input, opts);
      if (isCartWrite) {
        promise.then(() => setTimeout(() => { update(); init(); }, 50)).catch(() => {});
      }
      return promise;
    };
  }

  // Watch for dynamically-inserted bars (cart drawer opens, modals, etc.)
  if (window.MutationObserver) {
    const observer = new MutationObserver(() => {
      const bars = getBars();
      let unrendered = false;
      bars.forEach(b => { if (!b.dataset.gpRendered) { b.dataset.gpRendered = '1'; unrendered = true; } });
      if (unrendered) update();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
